/**
 * Language, country and currency for signup v2, from the browser's own settings (no IP lookup, no
 * external API): the time zone (Intl) says where the visitor is, the browser languages say which
 * language they read. Everything can be changed by the merchant afterwards.
 */

export const SIGNUP_LANGUAGES = ['es', 'en', 'pt'];
export const DEFAULT_LANGUAGE = 'es';
export const DEFAULT_COUNTRY = 'CO';

/** Store currency per country (ISO 3166-1 alpha-2), same data as the API's `GET /countries`. */
export const COUNTRY_CURRENCY = {
    AD: 'EUR', AE: 'AED', AF: 'AFN', AG: 'XCD', AI: 'XCD', AL: 'ALL', AM: 'AMD', AO: 'AOA',
    AQ: 'USD', AR: 'ARS', AS: 'USD', AT: 'EUR', AU: 'AUD', AW: 'AWG', AX: 'EUR', AZ: 'AZN',
    BA: 'BAM', BB: 'BBD', BD: 'BDT', BE: 'EUR', BF: 'XOF', BG: 'BGN', BH: 'BHD', BI: 'BIF',
    BJ: 'XOF', BL: 'EUR', BM: 'BMD', BN: 'BND', BO: 'BOB', BR: 'BRL', BS: 'BSD', BT: 'BTN',
    BV: 'NOK', BW: 'BWP', BY: 'BYN', BZ: 'BZD', CA: 'CAD', CC: 'AUD', CD: 'CDF', CF: 'XAF',
    CG: 'XAF', CH: 'CHF', CI: 'XOF', CK: 'NZD', CL: 'CLP', CM: 'XAF', CN: 'CNY', CO: 'COP',
    CR: 'CRC', CU: 'CUP', CV: 'CVE', CW: 'ANG', CX: 'AUD', CY: 'EUR', CZ: 'CZK', DE: 'EUR',
    DJ: 'DJF', DK: 'DKK', DM: 'XCD', DO: 'DOP', DZ: 'DZD', EC: 'USD', EE: 'EUR', EG: 'EGP',
    EH: 'MAD', ER: 'ERN', ES: 'EUR', ET: 'ETB', FI: 'EUR', FJ: 'FJD', FK: 'FKP', FM: 'USD',
    FO: 'DKK', FR: 'EUR', GA: 'XAF', GB: 'GBP', GD: 'XCD', GE: 'GEL', GF: 'EUR', GG: 'GBP',
    GH: 'GHS', GI: 'GIP', GL: 'DKK', GM: 'GMD', GN: 'GNF', GP: 'EUR', GQ: 'XAF', GR: 'EUR',
    GS: 'GBP', GT: 'GTQ', GU: 'USD', GW: 'XOF', GY: 'GYD', HK: 'HKD', HM: 'AUD', HN: 'HNL',
    HR: 'HRK', HT: 'HTG', HU: 'HUF', ID: 'IDR', IE: 'EUR', IL: 'ILS', IM: 'GBP', IN: 'INR',
    IO: 'USD', IQ: 'IQD', IR: 'IRR', IS: 'ISK', IT: 'EUR', JE: 'GBP', JM: 'JMD', JO: 'JOD',
    JP: 'JPY', KE: 'KES', KG: 'KGS', KH: 'KHR', KI: 'AUD', KM: 'KMF', KN: 'XCD', KP: 'KPW',
    KR: 'KRW', KW: 'KWD', KY: 'KYD', KZ: 'KZT', LA: 'LAK', LB: 'LBP', LC: 'XCD', LI: 'CHF',
    LK: 'LKR', LR: 'LRD', LS: 'LSL', LT: 'EUR', LU: 'EUR', LV: 'EUR', LY: 'LYD', MA: 'MAD',
    MC: 'EUR', MD: 'MDL', ME: 'EUR', MF: 'EUR', MG: 'MGA', MH: 'USD', MK: 'MKD', ML: 'XOF',
    MM: 'MMK', MN: 'MNT', MO: 'MOP', MP: 'USD', MQ: 'EUR', MR: 'MRU', MS: 'XCD', MT: 'EUR',
    MU: 'MUR', MV: 'MVR', MW: 'MWK', MX: 'MXN', MY: 'MYR', MZ: 'MZN', NA: 'NAD', NC: 'XPF',
    NE: 'XOF', NF: 'AUD', NG: 'NGN', NI: 'NIO', NL: 'EUR', NO: 'NOK', NP: 'NPR', NR: 'AUD',
    NU: 'NZD', NZ: 'NZD', OM: 'OMR', PA: 'PAB', PE: 'PEN', PF: 'XPF', PG: 'PGK', PH: 'PHP',
    PK: 'PKR', PL: 'PLN', PM: 'EUR', PN: 'NZD', PR: 'USD', PS: 'ILS', PT: 'EUR', PW: 'USD',
    PY: 'PYG', QA: 'QAR', RE: 'EUR', RO: 'RON', RS: 'RSD', RU: 'RUB', RW: 'RWF', SA: 'SAR',
    SB: 'SBD', SC: 'SCR', SD: 'SDG', SE: 'SEK', SG: 'SGD', SH: 'SHP', SI: 'EUR', SJ: 'NOK',
    SK: 'EUR', SL: 'SLL', SM: 'EUR', SN: 'XOF', SO: 'SOS', SR: 'SRD', SS: 'SSP', ST: 'STN',
    SV: 'SVC', SX: 'ANG', SY: 'SYP', SZ: 'SZL', TC: 'USD', TD: 'XAF', TF: 'EUR', TG: 'XOF',
    TH: 'THB', TJ: 'TJS', TK: 'NZD', TL: 'USD', TM: 'TMT', TN: 'TND', TO: 'TOP', TR: 'TRY',
    TT: 'TTD', TV: 'AUD', TW: 'TWD', TZ: 'TZS', UA: 'UAH', UG: 'UGX', US: 'USD', UY: 'UYU',
    UZ: 'UZS', VA: 'EUR', VC: 'XCD', VE: 'VES', VG: 'USD', VI: 'USD', VN: 'VND', VU: 'VUV',
    WF: 'XPF', WS: 'WST', XK: 'USD', YE: 'YER', YT: 'EUR', ZA: 'ZAR', ZM: 'ZMW', ZW: 'ZWL',
};

/** Currencies shown first in the selector (main markets), then the rest alphabetically. */
const PRIORITY_CURRENCIES = ['COP', 'MXN', 'USD', 'PEN', 'CLP', 'ARS', 'BRL', 'EUR', 'GTQ', 'CRC', 'DOP'];

/** Time zone → country for the zones where most visitors come from (Intl gives the zone only). */
const ZONE_COUNTRY = {
    'America/Bogota': 'CO',
    'America/Lima': 'PE',
    'America/Guayaquil': 'EC',
    'Pacific/Galapagos': 'EC',
    'America/Santiago': 'CL',
    'America/Punta_Arenas': 'CL',
    'America/Caracas': 'VE',
    'America/Panama': 'PA',
    'America/Costa_Rica': 'CR',
    'America/Guatemala': 'GT',
    'America/Santo_Domingo': 'DO',
    'America/El_Salvador': 'SV',
    'America/Tegucigalpa': 'HN',
    'America/Managua': 'NI',
    'America/Asuncion': 'PY',
    'America/Montevideo': 'UY',
    'America/La_Paz': 'BO',
    'America/Puerto_Rico': 'PR',
    'America/Havana': 'CU',
    'Europe/Madrid': 'ES',
    'Atlantic/Canary': 'ES',
    'Europe/Lisbon': 'PT',
    'Atlantic/Azores': 'PT',
    'Atlantic/Madeira': 'PT',
};

const ZONE_PREFIX_COUNTRY = [
    ['America/Argentina/', 'AR'],
    ['America/Mexico_City', 'MX'],
    ['America/Monterrey', 'MX'],
    ['America/Cancun', 'MX'],
    ['America/Merida', 'MX'],
    ['America/Tijuana', 'MX'],
    ['America/Chihuahua', 'MX'],
    ['America/Hermosillo', 'MX'],
    ['America/Mazatlan', 'MX'],
    ['America/Matamoros', 'MX'],
    ['America/Bahia_Banderas', 'MX'],
    ['America/Sao_Paulo', 'BR'],
    ['America/Fortaleza', 'BR'],
    ['America/Recife', 'BR'],
    ['America/Bahia', 'BR'],
    ['America/Belem', 'BR'],
    ['America/Manaus', 'BR'],
    ['America/Cuiaba', 'BR'],
    ['America/Campo_Grande', 'BR'],
    ['America/Porto_Velho', 'BR'],
    ['America/Maceio', 'BR'],
    ['America/Araguaina', 'BR'],
    ['America/Rio_Branco', 'BR'],
    ['America/New_York', 'US'],
    ['America/Chicago', 'US'],
    ['America/Denver', 'US'],
    ['America/Phoenix', 'US'],
    ['America/Los_Angeles', 'US'],
    ['America/Anchorage', 'US'],
    ['America/Detroit', 'US'],
    ['America/Indiana/', 'US'],
    ['America/Kentucky/', 'US'],
    ['Pacific/Honolulu', 'US'],
];

const browserLanguages = () => {
    try {
        const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
        return list.filter(Boolean);
    } catch {
        return [];
    }
};

const timeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
};

/** 'es' | 'en' | 'pt' from the browser languages (first supported one), Spanish otherwise. */
export const detectLanguage = () => {
    for (const tag of browserLanguages()) {
        const base = String(tag).toLowerCase().split('-')[0];
        if (SIGNUP_LANGUAGES.includes(base)) return base;
    }
    return DEFAULT_LANGUAGE;
};

/** Country of the visitor: time zone first (where they are), then the browser language region. */
export const detectCountry = () => {
    const zone = timeZone();
    if (ZONE_COUNTRY[zone]) return ZONE_COUNTRY[zone];
    const byPrefix = ZONE_PREFIX_COUNTRY.find(([prefix]) => zone.indexOf(prefix) === 0);
    if (byPrefix) return byPrefix[1];
    for (const tag of browserLanguages()) {
        const region = String(tag).split('-')[1];
        if (region && /^[A-Za-z]{2}$/.test(region) && COUNTRY_CURRENCY[region.toUpperCase()]) {
            return region.toUpperCase();
        }
    }
    return DEFAULT_COUNTRY;
};

export const currencyForCountry = (isoCode) => COUNTRY_CURRENCY[String(isoCode || '').toUpperCase()] || 'COP';

/** BCP 47 locale for number formatting: 'es' + 'CO' → 'es-CO'. */
export const formatLocale = (language, isoCode) => `${language || DEFAULT_LANGUAGE}-${isoCode || DEFAULT_COUNTRY}`;

const safeFormat = (locale, options) => {
    try {
        return new Intl.NumberFormat(locale, options);
    } catch {
        return new Intl.NumberFormat('es-CO', { ...options, currency: options.currency ? 'COP' : undefined });
    }
};

/** Decimals the currency uses (COP / CLP 0, USD / EUR 2…). */
export const currencyDecimals = (currency) =>
    safeFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;

/**
 * How to show the currency: the locale's symbol ('US$' for dollars in Brazil or Colombia, 'R$',
 * 'S/'…), or the narrow one when the symbol would be the bare code ('COP 1,235' in English → '$1,235').
 */
const currencyDisplayFor = (currency, locale) => {
    try {
        const part = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'symbol' })
            .formatToParts(0)
            .find((p) => p.type === 'currency');
        return part && part.value === currency ? 'narrowSymbol' : 'symbol';
    } catch {
        return 'symbol';
    }
};

/** '$', 'US$', 'R$', '€', 'S/'… as it reads in `locale`. */
export const currencySymbol = (currency, locale = 'es-CO') => {
    try {
        const part = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            currencyDisplay: currencyDisplayFor(currency, locale),
        })
            .formatToParts(0)
            .find((p) => p.type === 'currency');
        if (part) return part.value;
    } catch {
        /* fall through */
    }
    return '$';
};

/** Price with the store currency in the visitor's locale: 49900 COP → '$ 49.900', 19.9 USD (pt-BR) → 'US$ 19,90'. */
export const formatMoney = (value, currency = 'COP', locale = 'es-CO') =>
    safeFormat(locale, { style: 'currency', currency, currencyDisplay: currencyDisplayFor(currency, locale) }).format(
        Number(value) || 0
    );

/** [{ code, name }] for the currency selector, names in the UI language. */
export const getCurrencyOptions = (language = DEFAULT_LANGUAGE) => {
    let names = null;
    try {
        names = new Intl.DisplayNames([language], { type: 'currency' });
    } catch {
        names = null;
    }
    const nameOf = (code) => {
        try {
            return (names && names.of(code)) || code;
        } catch {
            return code;
        }
    };
    const codes = Array.from(new Set(Object.values(COUNTRY_CURRENCY)));
    const rest = codes.filter((c) => !PRIORITY_CURRENCIES.includes(c)).sort();
    return [...PRIORITY_CURRENCIES, ...rest].map((code) => ({ code, name: nameOf(code) }));
};
